import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Loader2 } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"

interface RenameReportListDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentName: string
  isPending?: boolean
  onConfirm: (newName: string) => void
}

function RenameForm({
  currentName,
  isPending,
  onConfirm,
  onCancel,
}: Readonly<{
  currentName: string
  isPending?: boolean
  onConfirm: (newName: string) => void
  onCancel: () => void
}>) {
  const { t } = useTranslation()
  const [name, setName] = useState(currentName)

  const isValid = !!name.trim() && name.trim() !== currentName

  const handleConfirm = () => {
    if (isValid && !isPending) {
      onConfirm(name.trim())
    }
  }

  return (
    <>
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label htmlFor="new-report-list-name">{t("dialogs.renameList.newNameLabel")}</Label>
          <Input
            id="new-report-list-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleConfirm()
              if (e.key === "Escape") onCancel()
            }}
            placeholder={t("dialogs.createList.namePlaceholder")}
            disabled={isPending}
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={isPending}>
          {t("actions.cancel")}
        </Button>
        <Button onClick={handleConfirm} disabled={!isValid || isPending}>
          {isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("common.loading")}
            </>
          ) : (
            t("actions.rename")
          )}
        </Button>
      </DialogFooter>
    </>
  )
}

export function RenameReportListDialog({
  open,
  onOpenChange,
  currentName,
  isPending,
  onConfirm,
}: Readonly<RenameReportListDialogProps>) {
  const { t } = useTranslation()
  return (
    <Dialog open={open} onOpenChange={isPending ? undefined : onOpenChange}>
      <DialogContent
        className="sm:max-w-[500px]"
        onInteractOutside={isPending ? (e) => e.preventDefault() : undefined}
      >
        <DialogHeader>
          <DialogTitle>{t("dialogs.renameList.title")}</DialogTitle>
        </DialogHeader>
        {open && (
          <RenameForm
            currentName={currentName}
            isPending={isPending}
            onConfirm={(name) => {
              onConfirm(name)
            }}
            onCancel={() => {
              onOpenChange(false)
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
